'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { 
  User, 
  Settings, 
  LogOut, 
  Shield
} from "lucide-react";
import { useAuth } from "@/context/auth-context";
import { cn } from "@/lib/utils";

interface UserProfileMenuProps {
  className?: string;
}

export function UserProfileMenu({ className }: UserProfileMenuProps) {
  const [open, setOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const router = useRouter();

  const handleNavigation = (href: string) => {
    setOpen(false);
    router.push(href);
  };

  const handleLogout = () => {
    setOpen(false);
    logout();
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn("h-8 w-8 rounded-full p-0", className)}
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.photoURL || "https://picsum.photos/100"} alt="User Avatar" />
            <AvatarFallback>{user.displayName?.charAt(0) || 'U'}</AvatarFallback>
          </Avatar>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user.photoURL || "https://picsum.photos/100"} alt="User Avatar" />
              <AvatarFallback>{user.displayName?.charAt(0) || 'U'}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.displayName || "Umpire"}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
        </div>
        
        <Separator />
        
        <div className="p-2">
          <Button
            variant="ghost"
            className="w-full justify-start h-8 px-2 text-sm font-normal"
            onClick={() => handleNavigation('/account')}
          >
            <User className="h-4 w-4 mr-2" />
            Profile
          </Button>
          
          <Button
            variant="ghost"
            className="w-full justify-start h-8 px-2 text-sm font-normal"
            onClick={() => handleNavigation('/settings')}
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          
          
          {isAdmin && (
            <Button
              variant="ghost"
              className="w-full justify-start h-8 px-2 text-sm font-normal"
              onClick={() => handleNavigation('/admin')}
            >
              <Shield className="h-4 w-4 mr-2" />
              Admin Panel
            </Button>
          )}
        </div>
        
        <Separator />
        
        <div className="p-2">
          <Button
            variant="ghost"
            className="w-full justify-start h-8 px-2 text-sm font-normal text-destructive hover:text-destructive"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
